import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '@/context/AdminContext';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminService } from '@/services/adminApi';
import AdminSidebar from '@/components/admin/AdminSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Users, Search, RefreshCw, ChevronLeft, ChevronRight,
  Mail, Download, Filter, X, Globe, Menu, FileSpreadsheet,
  Target, Smartphone, TrendingUp, BarChart3, PieChart as PieChartIcon,
  CheckCircle, XCircle, Send, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, BarChart, Bar
} from 'recharts';

// Excel export utility
const exportToExcel = (data, filename) => {
  const headers = Object.keys(data[0] || {});
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(h => {
        const val = row[h] || '';
        if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

// Colors for charts
const COLORS = ['#5BA946', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16'];

// CV Goal labels
const GOAL_LABELS = {
  looking_now: { fr: 'Cherche emploi', en: 'Looking now' },
  preparing: { fr: 'Préparation', en: 'Preparing' },
  improving: { fr: 'Amélioration', en: 'Improving' },
  recruiter: { fr: 'Recruteur', en: 'Recruiter' },
  student: { fr: 'Étudiant', en: 'Student' },
  other: { fr: 'Autre', en: 'Other' }
};

// Stat Card Component
function StatCard({ title, value, subtitle, icon: Icon, color, colors }) {
  const colorClasses = {
    green: 'bg-green-500/10 text-green-500',
    blue: 'bg-blue-500/10 text-blue-500',
    purple: 'bg-purple-500/10 text-purple-500',
    orange: 'bg-orange-500/10 text-orange-500',
    red: 'bg-red-500/10 text-red-500',
  };

  return (
    <Card className={`${colors.bgCard} ${colors.border}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className={`${colors.textSecondary} text-xs font-medium`}>{title}</p>
            <p className={`text-2xl font-bold ${colors.text} mt-1`}>{value}</p>
            {subtitle && <p className={`text-xs ${colors.textMuted} mt-1`}>{subtitle}</p>}
          </div>
          <div className={`p-2 rounded-lg ${colorClasses[color] || colorClasses.blue}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminLeads() {
  const { isAuthenticated, loading: authLoading } = useAdmin();
  const { colors, isDark } = useAdminTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Data states
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [countries, setCountries] = useState([]);
  
  // Filter states
  const [search, setSearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedGoal, setSelectedGoal] = useState('all');
  const [hasApp, setHasApp] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const limit = 20;
  
  // Mobile sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      loadLeads();
    }
  }, [isAuthenticated, page, search, selectedCountry, selectedGoal, hasApp, dateFrom, dateTo]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, chartRes, countriesRes] = await Promise.all([
        adminService.getLeadsStats(),
        adminService.getLeadsChart(14),
        adminService.getLeadsCountries()
      ]);
      setStats(statsRes.data);
      setChartData(chartRes.data || []);
      setCountries(countriesRes.data.countries || []);
    } catch (error) {
      console.error('Failed to load leads data:', error);
      toast.error('Failed to load leads data');
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async () => {
    try {
      const params = {
        limit,
        offset: page * limit,
        search: search || undefined,
        country: selectedCountry !== 'all' ? selectedCountry : undefined,
        cv_goal: selectedGoal !== 'all' ? selectedGoal : undefined,
        has_app: hasApp === 'all' ? undefined : hasApp === 'yes',
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined
      };
      
      const res = await adminService.getLeadsList(params);
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to load leads');
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = {
        search: search || undefined,
        country: selectedCountry !== 'all' ? selectedCountry : undefined,
        cv_goal: selectedGoal !== 'all' ? selectedGoal : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined
      };
      
      const res = await adminService.exportLeads(params);
      if (res.data.data && res.data.data.length > 0) {
        exportToExcel(res.data.data, 'edomatch_leads');
        toast.success(`Exported ${res.data.total} leads`);
      } else {
        toast.info('No data to export');
      }
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCountry('all');
    setSelectedGoal('all');
    setHasApp('all');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const hasActiveFilters = search || selectedCountry !== 'all' || selectedGoal !== 'all' || hasApp !== 'all' || dateFrom || dateTo;

  if (authLoading || loading) {
    return (
      <div className={`min-h-screen ${colors.bg} flex items-center justify-center`}>
        <div className="w-8 h-8 border-4 border-[#5BA946] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${colors.bg} flex`}>
      <AdminSidebar 
        currentPath={location.pathname}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className={`${colors.bgHeader} border-b ${colors.border} p-4 sm:p-6`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setSidebarOpen(true)}
                variant="ghost"
                size="icon"
                className={`lg:hidden ${colors.textSecondary}`}
              >
                <Menu className="w-6 h-6" />
              </Button>
              <div>
                <h1 className={`text-xl sm:text-2xl font-bold ${colors.text}`}>Leads & Downloads</h1>
                <p className={`${colors.textSecondary} text-sm mt-1`}>
                  {stats?.total_leads || 0} total leads captured
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={handleExport}
                disabled={exporting}
                variant="outline"
                size="sm"
                className={`${colors.btnOutline} hidden sm:flex`}
              >
                <FileSpreadsheet className={`w-4 h-4 ${exporting ? 'animate-spin' : ''} mr-2`} />
                Export Excel
              </Button>
              <Button 
                onClick={() => { loadData(); loadLeads(); }}
                variant="outline"
                size="sm"
                className={colors.btnOutline}
              >
                <RefreshCw className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard
              title="Total Leads"
              value={stats?.total_leads || 0}
              subtitle={`${stats?.leads_today || 0} today`}
              icon={Users}
              color="blue"
              colors={colors}
            />
            <StatCard
              title="This Week"
              value={stats?.leads_week || 0}
              subtitle="New leads"
              icon={TrendingUp}
              color="green"
              colors={colors}
            />
            <StatCard
              title="Email Sent Rate"
              value={`${stats?.email_sent_rate || 0}%`}
              subtitle={`${stats?.emails_sent || 0} emails`}
              icon={Send}
              color="purple"
              colors={colors}
            />
            <StatCard
              title="Want EdoMatch"
              value={stats?.want_import || 0}
              subtitle="Ready to convert"
              icon={Smartphone}
              color="orange"
              colors={colors}
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Leads Over Time */}
            <Card className={`${colors.bgCard} ${colors.border}`}>
              <CardHeader>
                <CardTitle className={`${colors.text} flex items-center gap-2`}>
                  <BarChart3 className="w-5 h-5 text-[#5BA946]" />
                  Leads (Last 14 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#5BA946" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#5BA946" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                      <XAxis 
                        dataKey="date" 
                        stroke={isDark ? '#6b7280' : '#9ca3af'}
                        fontSize={12}
                        tickFormatter={(value) => value.slice(5)}
                      />
                      <YAxis stroke={isDark ? '#6b7280' : '#9ca3af'} fontSize={12} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: isDark ? '#1f2937' : '#fff',
                          border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                          borderRadius: '8px'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="value" 
                        stroke="#5BA946" 
                        fillOpacity={1}
                        fill="url(#colorLeads)" 
                        name="Leads"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* CV Goals Distribution */}
            <Card className={`${colors.bgCard} ${colors.border}`}>
              <CardHeader>
                <CardTitle className={`${colors.text} flex items-center gap-2`}>
                  <Target className="w-5 h-5 text-blue-500" />
                  CV Goals Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {stats?.by_goal && stats.by_goal.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={stats.by_goal}
                          dataKey="count"
                          nameKey="goal"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ goal, percent }) => `${GOAL_LABELS[goal]?.en || goal}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {stats.by_goal.map((_, index) => (
                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: isDark ? '#1f2937' : '#fff',
                            border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className={`h-full flex items-center justify-center ${colors.textMuted}`}>
                      No goal data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Has App & Country Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Has EdoMatch App */}
            <Card className={`${colors.bgCard} ${colors.border}`}>
              <CardHeader>
                <CardTitle className={`${colors.text} flex items-center gap-2`}>
                  <Smartphone className="w-5 h-5 text-purple-500" />
                  EdoMatch App Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className={`p-4 ${isDark ? 'bg-green-900/20' : 'bg-green-50'} rounded-lg text-center`}>
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className={`text-2xl font-bold ${colors.text}`}>{stats?.has_app?.yes || 0}</p>
                    <p className={`text-sm ${colors.textSecondary}`}>Have the app</p>
                  </div>
                  <div className={`p-4 ${isDark ? 'bg-orange-900/20' : 'bg-orange-50'} rounded-lg text-center`}>
                    <XCircle className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                    <p className={`text-2xl font-bold ${colors.text}`}>{stats?.has_app?.no || 0}</p>
                    <p className={`text-sm ${colors.textSecondary}`}>Don't have it</p>
                  </div>
                </div>
                <div className="mt-4">
                  <div className={`text-center p-3 ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} rounded-lg`}>
                    <p className={`text-sm ${colors.textSecondary}`}>Conversion opportunity</p>
                    <p className={`text-xl font-bold text-blue-500`}>
                      {stats?.has_app?.no || 0} potential users
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* By Country */}
            <Card className={`${colors.bgCard} ${colors.border}`}>
              <CardHeader>
                <CardTitle className={`${colors.text} flex items-center gap-2`}>
                  <Globe className="w-5 h-5 text-[#5BA946]" />
                  Leads by Country
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {stats?.by_country && stats.by_country.length > 0 ? (
                    stats.by_country.map((item, idx) => (
                      <div key={idx} className={`flex items-center justify-between p-2 ${isDark ? 'bg-slate-700/30' : 'bg-gray-50'} rounded-lg`}>
                        <span className={colors.text}>{item.country}</span>
                        <Badge variant="secondary">{item.count}</Badge>
                      </div>
                    ))
                  ) : (
                    <p className={colors.textMuted}>No country data</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters Section */}
          <Card className={`${colors.bgCard} ${colors.border}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className={`${colors.text} text-lg flex items-center gap-2`}>
                  <Filter className="w-5 h-5" />
                  Lead Filters
                </CardTitle>
                <div className="flex items-center gap-2">
                  {hasActiveFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-500">
                      <X className="w-4 h-4 mr-1" /> Clear
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setShowFilters(!showFilters)} className={colors.textSecondary}>
                    {showFilters ? 'Hide' : 'Show'} Filters
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className={showFilters ? '' : 'hidden sm:block'}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Search */}
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${colors.textMuted}`} />
                  <Input
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                    placeholder="Search email..."
                    className={`pl-10 ${colors.bgInput} ${colors.border} ${colors.text}`}
                  />
                </div>

                {/* Country */}
                <Select value={selectedCountry} onValueChange={(v) => { setSelectedCountry(v); setPage(0); }}>
                  <SelectTrigger className={`${colors.bgInput} ${colors.border} ${colors.text}`}>
                    <Globe className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {countries.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* CV Goal */}
                <Select value={selectedGoal} onValueChange={(v) => { setSelectedGoal(v); setPage(0); }}>
                  <SelectTrigger className={`${colors.bgInput} ${colors.border} ${colors.text}`}>
                    <Target className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="All Goals" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Goals</SelectItem>
                    <SelectItem value="looking_now">Looking for job</SelectItem>
                    <SelectItem value="preparing">Preparing</SelectItem>
                    <SelectItem value="improving">Improving CV</SelectItem>
                    <SelectItem value="recruiter">Recruiter</SelectItem>
                    <SelectItem value="student">Student</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>

                {/* Has App */}
                <Select value={hasApp} onValueChange={(v) => { setHasApp(v); setPage(0); }}>
                  <SelectTrigger className={`${colors.bgInput} ${colors.border} ${colors.text}`}>
                    <Smartphone className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="App Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="yes">Has EdoMatch</SelectItem>
                    <SelectItem value="no">Doesn't have</SelectItem>
                  </SelectContent>
                </Select>

                {/* Date From */}
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                  className={`${colors.bgInput} ${colors.border} ${colors.text}`}
                />

                {/* Date To */}
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                  className={`${colors.bgInput} ${colors.border} ${colors.text}`}
                />

                {/* Mobile Export */}
                <Button onClick={handleExport} disabled={exporting} className="sm:hidden bg-[#5BA946]">
                  <Download className="w-4 h-4 mr-2" /> Export
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Leads Table */}
          <Card className={`${colors.bgCard} ${colors.border}`}>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${colors.border}`}>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium`}>Email</th>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium hidden md:table-cell`}>Country</th>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium hidden lg:table-cell`}>CV Goal</th>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium hidden sm:table-cell`}>EdoMatch App</th>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium hidden xl:table-cell`}>Email Sent</th>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium`}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.length === 0 ? (
                      <tr>
                        <td colSpan={6} className={`p-8 text-center ${colors.textMuted}`}>
                          No leads found
                        </td>
                      </tr>
                    ) : leads.map((lead) => (
                      <tr key={lead.id} className={`border-b ${colors.border} ${colors.bgHover}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Mail className={`w-4 h-4 ${colors.textMuted}`} />
                            <span className={`${colors.text} truncate max-w-[200px]`}>{lead.email}</span>
                          </div>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <Badge variant="outline">{lead.country || 'Unknown'}</Badge>
                        </td>
                        <td className="p-4 hidden lg:table-cell">
                          <span className={colors.textSecondary}>
                            {GOAL_LABELS[lead.cv_goal]?.en || lead.cv_goal}
                          </span>
                        </td>
                        <td className="p-4 hidden sm:table-cell">
                          {lead.has_edomatch_app ? (
                            <Badge className="bg-green-500/10 text-green-500 hover:bg-green-500/20">
                              <CheckCircle className="w-3 h-3 mr-1" /> Yes
                            </Badge>
                          ) : (
                            <Badge className="bg-orange-500/10 text-orange-500 hover:bg-orange-500/20">
                              <XCircle className="w-3 h-3 mr-1" /> No
                            </Badge>
                          )}
                        </td>
                        <td className="p-4 hidden xl:table-cell">
                          {lead.email_sent ? (
                            <Badge className="bg-blue-500/10 text-blue-500">Sent</Badge>
                          ) : (
                            <Badge variant="outline">Pending</Badge>
                          )}
                        </td>
                        <td className="p-4">
                          <span className={`${colors.textSecondary} text-sm flex items-center gap-1`}>
                            <Calendar className="w-3 h-3" />
                            {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className={`flex items-center justify-between p-4 border-t ${colors.border}`}>
                <p className={`text-sm ${colors.textSecondary}`}>
                  {total > 0 ? `${page * limit + 1}-${Math.min((page + 1) * limit, total)} of ${total}` : '0 results'}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                    className={colors.btnOutline}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(page + 1) * limit >= total}
                    onClick={() => setPage(p => p + 1)}
                    className={colors.btnOutline}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
